import { useEffect, useState } from 'react';
import { useGetStageDetailQuery, useGetPipelineRunQuery } from '@/services/api';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonRow } from '@/components/Skeleton';
import { redact } from '@/utils/redaction';
import { formatDuration, formatTime } from '@/utils/format';
import type { StageDetailDto, StageSummaryDto } from '@/types/api';
import { RoleOutputRenderer } from './RoleRenderers';

const ROLE_ICON: Record<string, { abbr: string; bg: string; fg: string }> = {
  explorer: { abbr: 'EX', bg: 'bg-role-explorer', fg: 'text-white' },
  architect: { abbr: 'AR', bg: 'bg-role-architect', fg: 'text-white' },
  coder: { abbr: 'CO', bg: 'bg-role-coder', fg: 'text-white' },
  reviewer: { abbr: 'RV', bg: 'bg-role-reviewer', fg: 'text-white' },
  tester: { abbr: 'TS', bg: 'bg-role-tester', fg: 'text-white' },
  documenter: { abbr: 'DC', bg: 'bg-role-documenter', fg: 'text-white' }
};

const STAGE_ROLE: Record<string, string> = {
  explore: 'explorer',
  plan: 'architect',
  code: 'coder',
  review: 'reviewer',
  test: 'tester',
  document: 'documenter'
};

export function StageOutputsTab({
  runId,
  stages,
  selectedStageId,
  onSelectStage,
  refreshIntervalMs = 0
}: {
  runId: string;
  stages: ReadonlyArray<StageSummaryDto>;
  selectedStageId: string | undefined;
  onSelectStage: (stageId: string) => void;
  refreshIntervalMs?: number;
}): JSX.Element {
  useEffect(() => {
    if (selectedStageId) return;
    const running = stages.find((s) => s.state === 'running');
    const completed = [...stages].reverse().find((s) => s.state === 'completed');
    const fallback = running ?? completed ?? stages[0];
    if (fallback) onSelectStage(fallback.stage_id);
  }, [selectedStageId, stages, onSelectStage]);

  return (
    <section className="grid grid-cols-[260px_1fr] gap-3.5 items-start" data-testid="stage-outputs-tab">
      <aside
        className="bg-panel border border-border rounded-lg overflow-hidden max-h-[calc(100vh-180px)] overflow-y-auto sticky top-0"
        aria-label="Stages"
      >
        <div className="px-3.5 py-2.5 font-semibold text-[12.5px] border-b border-border">
          Stages
        </div>
        {stages.map((s) => {
          const ico = ROLE_ICON[STAGE_ROLE[s.stage_id] ?? s.stage_id] ?? ROLE_ICON['explorer']!;
          const active = s.stage_id === selectedStageId;
          const dimmed = s.state === 'pending';
          return (
            <button
              key={s.stage_id}
              type="button"
              onClick={() => onSelectStage(s.stage_id)}
              className={`w-full text-left flex items-center gap-2.5 px-3.5 py-2.5 border-b border-border last:border-b-0 ${
                active ? 'bg-panel-2 border-l-[3px] border-l-accent pl-[11px]' : 'hover:bg-panel-2'
              } ${dimmed ? 'opacity-50' : ''}`}
              aria-pressed={active}
              data-testid={`stage-list-${s.stage_id}`}
            >
              <span
                className={`w-[18px] h-[18px] rounded-md grid place-items-center font-bold text-[9px] flex-none ${ico.bg} ${ico.fg}`}
              >
                {ico.abbr}
              </span>
              <span className="flex-1 min-w-0">
                <div className="text-[12.5px] font-medium">{s.stage_id}</div>
                <div className="text-[11px] text-text-muted mt-0.5">
                  {summarise(s)}
                </div>
              </span>
            </button>
          );
        })}
      </aside>

      <div
        className="bg-panel border border-border rounded-lg overflow-y-auto max-h-[calc(100vh-180px)]"
        data-testid="stage-detail-pane"
      >
        {selectedStageId ? (
          <StageDetailPane
            runId={runId}
            stageId={selectedStageId}
            stageState={stages.find((s) => s.stage_id === selectedStageId)?.state}
            refreshIntervalMs={refreshIntervalMs}
          />
        ) : (
          <div className="p-6 text-text-muted text-[12.5px]">
            Pick a stage to view its output.
          </div>
        )}
      </div>
    </section>
  );
}

function summarise(s: StageSummaryDto): string {
  if (s.state === 'completed') {
    return `${s.artefact_count} artefact${s.artefact_count === 1 ? '' : 's'} · ${formatDuration(s.started_at, s.completed_at)}`;
  }
  if (s.state === 'running') {
    return `${formatDuration(s.started_at, null)}`;
  }
  if (s.state === 'failed') {
    return `failed at ${s.completed_at ? formatTime(s.completed_at) : '—'}`;
  }
  if (s.state === 'paused_for_approval') return 'paused for approval';
  return 'pending';
}

function StageDetailPane({
  runId,
  stageId,
  stageState,
  refreshIntervalMs
}: {
  runId: string;
  stageId: string;
  stageState?: string;
  refreshIntervalMs: number;
}): JSX.Element {
  const isTerminal = stageState === 'completed' || stageState === 'failed';
  const shouldPoll = stageState !== undefined && !isTerminal;
  const pollMs = refreshIntervalMs > 0 ? refreshIntervalMs : 4000;
  const resp = useGetStageDetailQuery(
    { runId, stageId },
    shouldPoll ? { pollingInterval: pollMs, skipPollingIfUnfocused: true } : undefined
  );

  if (resp.isLoading) {
    return (
      <div className="p-4" data-testid="stage-detail-loading">
        <SkeletonRow widths={['35%', '60%', '80%', '70%', '90%', '55%']} />
      </div>
    );
  }
  if ((resp.isError || !resp.data) && (stageState === 'pending' || stageState === undefined)) {
    return (
      <div className="p-4" data-testid="stage-detail">
        <StageOutputPlaceholder state={stageState ?? 'pending'} />
      </div>
    );
  }
  if (resp.isError || !resp.data) {
    return (
      <div data-testid="stage-detail-error">
        <ErrorState
          title="Couldn't load stage output"
          body="This stage's output couldn't be loaded right now. The stage timeline and run header above are still current."
          onRetry={() => void resp.refetch()}
        />
      </div>
    );
  }

  const detail = resp.data;
  return (
    <div data-testid="stage-detail">
      <ProvenanceBreadcrumb detail={detail} />
      <div className="p-4">
        {detail.back_edge ? <BackEdgeStrip detail={detail} /> : null}
        {detail.partial_failure ? <PartialFailureBanner detail={detail} /> : null}
        {detail.sub_progress ? <SubProgress detail={detail} /> : null}
        {detail.output ? (
          <RoleOutputRenderer output={redactDisplay(detail.output)} />
        ) : (
          <StageOutputPlaceholder state={detail.state} />
        )}
      </div>
    </div>
  );
}

function StageOutputPlaceholder({ state }: { state?: string }): JSX.Element {
  const copy: { title: string; body: string } =
    state === 'pending'
      ? {
          title: 'Not started yet',
          body: 'This stage is waiting for the earlier stages to finish. Its output will appear here once it begins.'
        }
      : state === 'running'
        ? {
            title: 'In progress',
            body: 'This stage is running. Its output will appear here as the agent produces it.'
          }
        : state === 'paused_for_approval'
          ? {
              title: 'Waiting for approval',
              body: 'This stage is paused for a review decision. Output will appear here once it resumes.'
            }
          : state === 'failed'
            ? {
                title: 'No output produced',
                body: 'This stage did not finish, so it produced no output. See the Errors tab for the cause.'
              }
            : {
                title: 'No output for this stage',
                body: 'This stage finished without producing structured output to display.'
              };
  return (
    <div className="py-10 text-center" data-testid="stage-output-placeholder">
      <div className="text-[13px] text-text font-medium mb-1">{copy.title}</div>
      <div className="text-[12px] text-text-muted max-w-[420px] mx-auto">{copy.body}</div>
    </div>
  );
}

function ProvenanceBreadcrumb({ detail }: { detail: StageDetailDto }): JSX.Element {
  const reclaimedAt = useGetPipelineRunQuery(detail.pipeline_run_id, {
    skip: !detail.pipeline_run_id
  }).data?.workspace_reclaimed_at;
  const p = detail.provenance;
  return (
    <div
      className="bg-panel-2 border-b border-border px-4 py-3 sticky top-0 z-10"
      data-testid="provenance-breadcrumb"
      aria-label="Artefact provenance"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="grid grid-cols-5 gap-3 flex-1 min-w-0">
          <ProvCell label="Produced by role" value={p.produced_by_role} />
          <ProvCell label="Agent id" value={p.produced_by_agent_id} mono />
          <ProvCell label="Produced at" value={formatTime(p.produced_at)} />
          <ProvCell label="Pipeline run id" value={p.pipeline_run_id} mono />
          <ProvCell label="Parent artefact" value={p.parent_artefact_ref ?? '—'} mono />
        </div>
        <div className="flex items-start gap-2">
          {detail.stage_id === 'code' ? (
            <>
              <DownloadCodeButton
                runId={detail.pipeline_run_id}
                stageId={detail.stage_id}
                kind="generated"
                label="↓ Generated code"
                title="Download only the files the coder produced this run (latest)"
              />
              <DownloadCodeButton
                runId={detail.pipeline_run_id}
                stageId={detail.stage_id}
                kind="full"
                label="↓ Full codebase"
                title="Download the complete codebase as it would ship to GitHub (latest)"
                reclaimedAt={reclaimedAt ?? null}
              />
            </>
          ) : null}
          {detail.stage_id === 'test' ? (
            <DownloadTestReportButton
              runId={detail.pipeline_run_id}
              stageId={detail.stage_id}
            />
          ) : null}
          {detail.raw_output_artefact_id && detail.stage_id !== 'code' ? (
            <DownloadRawOutputButton
              runId={detail.pipeline_run_id}
              stageId={detail.stage_id}
              artefactId={detail.raw_output_artefact_id}
              role={detail.role}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DownloadCodeButton({
  runId,
  stageId,
  kind,
  label,
  title,
  reclaimedAt
}: {
  runId: string;
  stageId: string;
  kind: 'generated' | 'full';
  label: string;
  title: string;
  reclaimedAt?: string | null;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reclaimed = Boolean(reclaimedAt);
  const onDownload = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const base = import.meta.env['VITE_API_BASE_URL'] ?? '';
      const token = localStorage.getItem('jwt');
      const url = `${base}/api/v1/pipelines/runs/${encodeURIComponent(runId)}/stages/${encodeURIComponent(stageId)}/code-download?kind=${kind}`;
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (resp.status === 404) {
        setErr(unavailableLabel(reclaimedAt));
        return;
      }
      if (!resp.ok) throw new Error(`download failed: ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filenameFromResponse(resp, kind === 'full' ? 'codebase.zip' : 'generated-code.zip');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setErr('Retry download');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      data-testid={`download-code-${kind}`}
      onClick={() => void onDownload()}
      disabled={busy || reclaimed}
      title={reclaimed ? unavailableLabel(reclaimedAt) : title}
      className="shrink-0 h-[28px] px-3 rounded-md border border-border bg-panel text-[11.5px] text-text hover:bg-panel-2 disabled:opacity-50"
    >
      {reclaimed ? unavailableLabel(reclaimedAt) : busy ? 'Downloading…' : (err ?? label)}
    </button>
  );
}

function unavailableLabel(reclaimedAt?: string | null): string {
  if (!reclaimedAt) return 'Working files are no longer available';
  const d = new Date(reclaimedAt);
  if (Number.isNaN(d.getTime())) return 'Working files are no longer available';
  return `Working files cleaned up on ${d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })}`;
}

function DownloadTestReportButton({
  runId,
  stageId
}: {
  runId: string;
  stageId: string;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const onDownload = async (): Promise<void> => {
    setBusy(true);
    setErr(false);
    try {
      const base = import.meta.env['VITE_API_BASE_URL'] ?? '';
      const token = localStorage.getItem('jwt');
      const url = `${base}/api/v1/pipelines/runs/${encodeURIComponent(runId)}/stages/${encodeURIComponent(stageId)}/test-report`;
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!resp.ok) throw new Error(`download failed: ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filenameFromResponse(resp, 'tester-results.md');
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      data-testid="download-test-report"
      onClick={() => void onDownload()}
      disabled={busy}
      title="Download the test cases, results, and evidence as a report"
      className="shrink-0 h-[28px] px-3 rounded-md border border-border bg-panel text-[11.5px] text-text hover:bg-panel-2 disabled:opacity-50"
    >
      {busy ? 'Downloading…' : err ? 'Retry download' : '↓ Test report'}
    </button>
  );
}

function filenameFromResponse(resp: Response, fallback: string): string {
  const cd = resp.headers.get('Content-Disposition') ?? '';
  const m = /filename="?([^"]+)"?/.exec(cd);
  return m?.[1] ?? fallback;
}

function DownloadRawOutputButton({
  runId,
  stageId,
  artefactId,
  role
}: {
  runId: string;
  stageId: string;
  artefactId: string;
  role: string;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const onDownload = async (): Promise<void> => {
    setBusy(true);
    setErr(false);
    try {
      const base = import.meta.env['VITE_API_BASE_URL'] ?? '';
      const token = localStorage.getItem('jwt');
      const url = `${base}/api/v1/pipelines/runs/${encodeURIComponent(runId)}/stages/${encodeURIComponent(stageId)}/artefacts/${encodeURIComponent(artefactId)}/download`;
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!resp.ok) throw new Error(`download failed: ${resp.status}`);
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${role}-${stageId}-raw-output.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      data-testid="download-raw-output"
      onClick={() => void onDownload()}
      disabled={busy}
      title="Download this role's raw model output"
      className="shrink-0 h-[28px] px-3 rounded-md border border-border bg-panel text-[11.5px] text-text hover:bg-panel-2 disabled:opacity-50"
    >
      {busy ? 'Downloading…' : err ? 'Retry download' : '↓ Raw output'}
    </button>
  );
}

function ProvCell({
  label,
  value,
  mono = false
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="text-[10px] text-text-muted uppercase tracking-wider mb-1">{label}</div>
      <div
        className={`text-[11.5px] text-text-dim overflow-hidden text-ellipsis whitespace-nowrap ${mono ? 'font-mono' : ''}`}
      >
        {value}
      </div>
    </div>
  );
}

function SubProgress({ detail }: { detail: StageDetailDto }): JSX.Element {
  const sp = detail.sub_progress;
  if (!sp) return <></>;
  return (
    <div className="mb-3" data-testid="sub-progress">
      <div className="text-[11.5px] text-text-dim mb-1 flex gap-2.5 items-center">
        <span className="font-mono text-text-muted">step:</span>
        <span>{sp.current_step}</span>
      </div>
      <div className="h-[6px] bg-panel-3 rounded overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-accent to-accent-2 transition-[width] duration-500"
          style={{ width: `${Math.round(sp.progress_pct)}%` }}
          data-testid="sub-progress-fill"
        />
      </div>
      <div className="text-[11.5px] text-text-muted flex gap-4 mt-1.5">
        <span>
          <strong className="text-text-dim font-medium">progress</strong>{' '}
          <span className="font-mono text-text-dim">{Math.round(sp.progress_pct)}%</span>
        </span>
        <span>
          <strong className="text-text-dim font-medium">latency_so_far</strong>{' '}
          <span className="font-mono text-text-dim">{Math.round(sp.latency_so_far_ms / 1000)} s</span>
        </span>
      </div>
    </div>
  );
}

function BackEdgeStrip({ detail }: { detail: StageDetailDto }): JSX.Element {
  const be = detail.back_edge;
  if (!be) return <></>;
  return (
    <div
      className="flex items-center gap-2.5 px-3 py-2.5 mb-3 rounded-md border border-warn/30 bg-warn/10 text-[12px] text-text-dim"
      data-testid="back-edge-strip"
      role="status"
    >
      <span className="text-warn text-base">↺</span>
      <div>
        <strong className="text-warn">Rework loop fired</strong> — {be.from_stage} → {be.to_stage}{' '}
        · {be.feedback_summary}
      </div>
      <span className="ml-auto text-[11.5px] text-text-muted tabular-nums">
        attempt {be.attempt_number} of {be.max_attempts}
      </span>
    </div>
  );
}

function PartialFailureBanner({ detail }: { detail: StageDetailDto }): JSX.Element {
  const pf = detail.partial_failure;
  if (!pf) return <></>;
  return (
    <div
      className="flex items-center gap-2.5 px-3 py-2.5 mb-3 rounded-md border border-err/30 bg-err/10 text-xs text-text-dim"
      data-testid="partial-failure-banner"
      role="alert"
    >
      <strong className="text-err">Partial failure:</strong>
      <span>
        branch <code className="font-mono">{pf.failed_branch_id}</code> · {pf.failure_reason}
      </span>
    </div>
  );
}

function redactDisplay<T>(value: T): T {
  return redact(value);
}

export { StageOutputsTab as default };
